import { computed, makeObservable, observable, runInAction } from 'mobx'

import { IdGenerator, ImageUploader, LocalImageFile } from '../ports'
import { Attachment } from '../protocol/types'
import { fold } from '../result'

export const MAX_ATTACHMENTS = 10

interface TrayItemBase {
  readonly id: string
  readonly file: LocalImageFile
}

export type TrayItem =
  | (TrayItemBase & { readonly state: 'uploading' })
  | (TrayItemBase & { readonly state: 'uploaded'; readonly url: string })
  | (TrayItemBase & { readonly state: 'failed' })

// The single owner of a composer's pending attachments: uploads start on
// add, only uploaded items are drained into a message, failed items stay
// visible for retry. A result landing after its item was removed is dropped.
export class AttachmentTrayStore {
  public items: TrayItem[] = []

  public constructor(
    private readonly uploadImage: ImageUploader,
    private readonly newId: IdGenerator,
  ) {
    makeObservable(this, {
      items: observable.ref,
      remainingSlots: computed,
      isUploading: computed,
      hasUploaded: computed,
    })
  }

  public get remainingSlots(): number {
    return Math.max(0, MAX_ATTACHMENTS - this.items.length)
  }

  public get isUploading(): boolean {
    return this.items.some(item => item.state === 'uploading')
  }

  public get hasUploaded(): boolean {
    return this.items.some(item => item.state === 'uploaded')
  }

  public add(files: readonly LocalImageFile[]): void {
    const accepted = files.slice(0, this.remainingSlots)
    const added: TrayItem[] = accepted.map(file => ({
      id: this.newId(),
      state: 'uploading',
      file,
    }))

    runInAction(() => {
      this.items = [...this.items, ...added]
    })
    added.forEach(item => this.upload(item.id))
  }

  public retry(id: string): void {
    const item = this.items.find(i => i.id === id && i.state === 'failed')
    if (!item) {
      return
    }

    this.replace(id, { id, state: 'uploading', file: item.file })
    this.upload(id)
  }

  public remove(id: string): void {
    runInAction(() => {
      this.items = this.items.filter(item => item.id !== id)
    })
  }

  public takeUploaded(): Attachment[] {
    const uploaded = this.items.filter(
      (item): item is TrayItem & { state: 'uploaded' } =>
        item.state === 'uploaded',
    )
    runInAction(() => {
      this.items = this.items.filter(item => item.state !== 'uploaded')
    })
    return uploaded.map(item => ({
      url: item.url,
      fileName: item.file.fileName,
      contentType: item.file.contentType,
      size: item.file.size,
      width: item.file.width,
      height: item.file.height,
    }))
  }

  private async upload(id: string): Promise<void> {
    const item = this.items.find(i => i.id === id)
    if (!item) {
      return
    }

    const result = await this.uploadImage(item.file)
    fold(
      result,
      url => this.replace(id, { id, state: 'uploaded', file: item.file, url }),
      () => this.replace(id, { id, state: 'failed', file: item.file }),
    )
  }

  private replace(id: string, next: TrayItem): void {
    runInAction(() => {
      this.items = this.items.map(item => (item.id === id ? next : item))
    })
  }
}
